import React from 'react';
import DiscordButton from './DiscordButton';
import './Tournaments.css';

// ─── Date helpers ────────────────────────────────────────────────────────────

// Returns the next N upcoming Fridays on or after today
const getUpcomingFridays = (count = 5) => {
  const dates = [];
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  // Advance to the next Friday (day 5)
  const day = d.getDay();
  const daysUntilFriday = (5 - day + 7) % 7;
  d.setDate(d.getDate() + daysUntilFriday);
  for (let i = 0; i < count; i++) {
    dates.push(new Date(d));
    d.setDate(d.getDate() + 7);
  }
  return dates;
};

// Fixed Capital Women's Tournament dates (every other Sunday)
const WOMENS_DATES = [
  new Date(2026, 8, 6),   // September  6
  new Date(2026, 8, 20),  // September 20
  new Date(2026, 9, 4),   // October  4
  new Date(2026, 9, 18),  // October 18
];

const MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];
const DAYS   = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

const formatDate = (d) =>
  `${DAYS[d.getDay()]}, ${MONTHS[d.getMonth()]} ${d.getDate()}`;

// Build a Google Calendar "add event" URL
const gcalUrl = ({ title, location, description, date, startHour, startMin = 0, durationHours = 3 }) => {
  const pad = (n) => String(n).padStart(2, '0');
  const fmt = (d) =>
    `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}T${pad(startHour)}${pad(startMin)}00`;
  const endDate = new Date(date);
  endDate.setHours(startHour + durationHours, startMin);
  const fmtEnd = (d) =>
    `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}T${pad(endDate.getHours())}${pad(endDate.getMinutes())}00`;
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${fmt(date)}/${fmtEnd(endDate)}`,
    location,
    details: description,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const LOCATION = '117 J St, Sacramento, CA 95814';

// ─── Date chip component ──────────────────────────────────────────────────────
const DateChip = ({ date, gcalParams }) => {
  const url = gcalUrl({ ...gcalParams, date });
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      style={styles.dateChip}
      title="Add to Google Calendar"
    >
      📅 {formatDate(date)}
    </a>
  );
};

// ─── Component ───────────────────────────────────────────────────────────────
const Tournaments = () => {
  const fridays = getUpcomingFridays(5);

  return (
    <div style={styles.container}>
      <h1 style={styles.pageTitle}>Tournaments</h1>
      <p style={styles.pageSubtitle}>
        Competitive pinball for every skill level — come play!
      </p>

      <div style={styles.discordRow}>
        <DiscordButton>Join the Discord</DiscordButton>
      </div>

      {/* ── Friday Night Strikes ── */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <span style={styles.emoji}>⚡</span>
          <div>
            <h2 style={styles.cardTitle}>Friday Night Strikes</h2>
            <p style={styles.cardFrequency}>Every Friday Night</p>
          </div>
        </div>
        <div style={styles.cardBody}>
          <div style={styles.highlight}>
            <span style={styles.highlightIcon}>🌟</span>
            <span style={styles.highlightText}>
              <strong>Beginner Friendly!</strong> Players are paired based on skill level so everyone gets competitive, fair matchups regardless of experience.
            </span>
          </div>

          <div style={styles.detailGrid}>
            <div className="t-detail" style={styles.detailItem}>
              <span style={styles.detailLabel}>Format</span>
              <span style={styles.detailValue}>4 Strikes Knockout · 1 vs 1</span>
            </div>
            <div className="t-detail" style={styles.detailItem}>
              <span style={styles.detailLabel}>Check-In</span>
              <span style={styles.detailValue}>7:00 PM</span>
            </div>
            <div className="t-detail" style={styles.detailItem}>
              <span style={styles.detailLabel}>Start Time</span>
              <span style={styles.detailValue}>7:30 PM</span>
            </div>
            <div className="t-detail" style={styles.detailItem}>
              <span style={styles.detailLabel}>Entry</span>
              <span style={styles.detailValue}>$5 Tournament Entry + $15 Unlimited Play = $20 total</span>
            </div>
            <div className="t-detail" style={styles.detailItem}>
              <span style={styles.detailLabel}>Game Selection</span>
              <span style={styles.detailValue}>Player 1 picks the game · All games on freeplay</span>
            </div>
          </div>

          <div style={styles.payoutBox}>
            <span style={styles.payoutTitle}>🏅 Prizes</span>
            <div style={styles.payoutRow}><span>🥇 1st Place</span><span>Cash Payout + Trophy</span></div>
            <div style={styles.payoutRow}><span>🥈 2nd Place</span><span>Cash Payout + Trophy</span></div>
            <div style={styles.payoutRow}><span>🥉 3rd Place</span><span>Cash Payout + Trophy</span></div>
            <p style={styles.payoutNote}>Prize pool based on nightly entries</p>
          </div>

          <div style={styles.datesSection}>
            <p style={styles.datesLabel}>Upcoming Dates — click to add to Google Calendar:</p>
            <div style={styles.dateChips}>
              {fridays.map((d, i) => (
                <DateChip
                  key={i}
                  date={d}
                  gcalParams={{
                    title: 'Friday Night Strikes at Capital Pinball Parlor',
                    location: LOCATION,
                    description: 'Check-in 7:00 PM · Start 7:30 PM\n4 Strikes Knockout · 1 vs 1\n$5 entry + $15 unlimited play\ncapitalpinballparlor.com',
                    startHour: 19,
                    startMin: 0,
                    durationHours: 3,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Capital Women's Tournament ── */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <span style={styles.emoji}>🎀</span>
          <div>
            <h2 style={styles.cardTitle}>Capital Women's Tournament</h2>
            <p style={styles.cardFrequency}>Women's Tournament · Every Other Sunday</p>
          </div>
        </div>
        <div style={styles.cardBody}>
          <div style={styles.detailGrid}>
            <div className="t-detail" style={styles.detailItem}>
              <span style={styles.detailLabel}>Check-In</span>
              <span style={styles.detailValue}>2:30 PM</span>
            </div>
            <div className="t-detail" style={styles.detailItem}>
              <span style={styles.detailLabel}>Start Time</span>
              <span style={styles.detailValue}>3:00 PM</span>
            </div>
            <div className="t-detail" style={styles.detailItem}>
              <span style={styles.detailLabel}>Format</span>
              <span style={styles.detailValue}>Group Match Play · 4 rounds of 4-player matchplay</span>
            </div>
            <div className="t-detail" style={styles.detailItem}>
              <span style={styles.detailLabel}>Finals</span>
              <span style={styles.detailValue}>Top 4 qualify · 3 rounds of 4-player matchplay</span>
            </div>
            <div className="t-detail" style={styles.detailItem}>
              <span style={styles.detailLabel}>Organizer</span>
              <span style={styles.detailValue}>Elsie Oster</span>
            </div>
          </div>

          <div style={styles.payoutBox}>
            <span style={styles.payoutTitle}>🏅 Prizes</span>
            <div style={styles.payoutRow}><span>🥇 1st Place</span><span>Cash Payout + Trophy</span></div>
            <div style={styles.payoutRow}><span>🥈 2nd Place</span><span>Cash Payout + Trophy</span></div>
            <div style={styles.payoutRow}><span>🥉 3rd Place</span><span>Cash Payout + Trophy</span></div>
            <div style={styles.payoutRow}><span>4th Place</span><span>Cash Payout + Trophy</span></div>
            <div style={styles.payoutRow}><span>🪣 Last Place</span><span>Trophy</span></div>
            <p style={styles.payoutNote}>Prize pool based on nightly entries</p>
          </div>

          <div style={styles.datesSection}>
            <p style={styles.datesLabel}>Upcoming Dates — click to add to Google Calendar:</p>
            <div style={styles.dateChips}>
              {WOMENS_DATES.map((d, i) => (
                <DateChip
                  key={i}
                  date={d}
                  gcalParams={{
                    title: "Capital Women's Tournament at Capital Pinball Parlor",
                    location: LOCATION,
                    description: "Check-in 2:30 PM · Start 3:00 PM\nWomen's Pinball Tournament · Group Match Play\ncapitalpinballparlor.com",
                    startHour: 15,
                    startMin: 0,
                    durationHours: 3,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '760px',
    margin: '0 auto',
    padding: 'clamp(18px, 4vw, 40px) clamp(14px, 4vw, 20px)',
    paddingTop: '10px',
    color: 'var(--text-primary)',
  },
  pageTitle: {
    fontFamily: 'var(--font-heading)',
    fontSize: 'clamp(1.55rem, 6.2vw, 2.4rem)',
    color: 'var(--accent-gold)',
    textAlign: 'center',
    marginBottom: '10px',
    textShadow: 'var(--accent-gold-glow)',
  },
  pageSubtitle: {
    fontFamily: "'Rajdhani', sans-serif",
    textAlign: 'center',
    color: 'var(--text-muted)',
    fontSize: 'clamp(0.98rem, 2.6vw, 1.1rem)',
    fontWeight: 500,
    marginBottom: 'clamp(22px, 4vw, 40px)',
  },
  discordRow: {
    textAlign: 'center',
    marginBottom: '30px',
  },
  card: {
    background: 'var(--card-bg)',
    borderRadius: '12px',
    marginBottom: '30px',
    overflow: 'hidden',
    border: '1px solid var(--card-border)',
    boxShadow: 'var(--card-shadow)',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    background: 'var(--card-header-bg)',
    padding: 'clamp(15px, 3vw, 20px) clamp(16px, 3.5vw, 24px)',
    borderBottom: '2px solid var(--card-header-border)',
  },
  emoji: {
    fontSize: '2rem',
    lineHeight: 1,
    filter: 'drop-shadow(0 0 6px rgba(255,200,0,0.6))',
  },
  cardTitle: {
    fontFamily: 'var(--font-heading)',
    fontSize: 'clamp(1.02rem, 3.6vw, 1.3rem)',
    color: 'var(--accent-gold)',
    margin: 0,
    lineHeight: 1.25,
    textShadow: 'var(--accent-gold-glow-dim)',
  },
  cardFrequency: {
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: '0.9rem',
    fontWeight: 600,
    color: 'var(--text-very-muted)',
    margin: '4px 0 0 0',
    letterSpacing: '0.5px',
  },
  cardBody: {
    padding: 'clamp(16px, 3.5vw, 20px) clamp(16px, 3.5vw, 24px)',
  },
  highlight: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    background: 'var(--highlight-bg)',
    border: '1px solid var(--highlight-border)',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '18px',
    boxShadow: 'var(--highlight-shadow)',
  },
  highlightIcon: {
    fontSize: '1.2rem',
    flexShrink: 0,
  },
  highlightText: {
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: '1rem',
    fontWeight: 600,
    color: 'var(--highlight-color)',
    lineHeight: '1.5',
  },
  detailGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '16px',
  },
  detailItem: {
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: '1rem',
    fontWeight: 500,
    lineHeight: '1.5',
  },
  detailLabel: {
    color: 'var(--detail-label)',
    fontWeight: '700',
    textTransform: 'uppercase',
    fontSize: '0.72rem',
    paddingTop: '3px',
    letterSpacing: '0.8px',
  },
  detailValue: {
    color: 'var(--detail-value)',
  },
  payoutBox: {
    background: 'var(--payout-bg)',
    border: '1px solid var(--payout-border)',
    borderRadius: '10px',
    padding: '14px 18px',
    marginBottom: '18px',
    boxShadow: 'var(--payout-shadow)',
  },
  payoutTitle: {
    display: 'block',
    fontFamily: 'var(--font-heading)',
    color: 'var(--accent-gold)',
    fontSize: '0.85rem',
    letterSpacing: '1px',
    marginBottom: '10px',
  },
  payoutRow: {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '4px 14px',
    textAlign: 'left',
    fontFamily: "'Rajdhani', sans-serif",
    fontWeight: 600,
    fontSize: '0.95rem',
    color: 'var(--text-secondary)',
    padding: '5px 0',
    borderBottom: '1px solid var(--payout-row-border)',
  },
  payoutNote: {
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: '0.78rem',
    color: 'var(--payout-note-color)',
    margin: '8px 0 0 0',
    fontStyle: 'italic',
  },
  datesSection: {
    marginBottom: '18px',
  },
  datesLabel: {
    fontFamily: "'Rajdhani', sans-serif",
    fontWeight: 600,
    fontSize: '0.82rem',
    color: 'var(--detail-label)',
    marginBottom: '8px',
    letterSpacing: '0.3px',
  },
  dateChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  dateChip: {
    display: 'inline-block',
    background: 'var(--chip-bg)',
    border: '1px solid var(--chip-border)',
    borderRadius: '20px',
    padding: '8px 15px',
    fontFamily: "'Rajdhani', sans-serif",
    fontWeight: 700,
    fontSize: '0.85rem',
    color: 'var(--chip-color)',
    textDecoration: 'none',
    boxShadow: 'var(--chip-shadow)',
    transition: 'box-shadow 0.2s, border-color 0.2s',
    cursor: 'pointer',
  },
};

export default Tournaments;
